import { address, contact, defaultDescription, defaultDescriptionEn, defaultTitle, geo, url } from 'data/config';
import React, { useContext } from 'react';

import Helmet from 'react-helmet';
import { LangContext } from 'providers/LangProvider';
import ShareImage from 'assets/thumbnail/hvik_tommi_8.jpg';

// Shown when the site is shared on social media or in messaging apps
const image = `${url}${ShareImage}`;

// For search engines: the farm as a place that is closed to the public, and the website about it, published by the
// owners with their email address as the contact
const structuredData = (lang, description) =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Place',
        '@id': `${url}/#place`,
        name: 'Hindisvík',
        alternateName: 'Hindisvik',
        description,
        url: lang === 'is' ? `${url}/` : `${url}/en/`,
        image,
        publicAccess: false,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: geo.latitude,
          longitude: geo.longitude,
        },
        hasMap: `https://www.openstreetmap.org/?mlat=${geo.latitude}&mlon=${geo.longitude}#map=13/${geo.latitude}/${geo.longitude}`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: address.locality,
          addressRegion: address.region,
          addressCountry: address.country,
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${url}/#website`,
        url: `${url}/`,
        name: defaultTitle,
        inLanguage: ['is', 'en'],
        about: { '@id': `${url}/#place` },
        publisher: {
          '@type': 'Organization',
          name: lang === 'is' ? 'Eigendur Hindisvíkur' : 'The owners of Hindisvík',
          email: contact.email,
        },
      },
    ],
  });

export const SEO = ({ title = defaultTitle, description: descriptionProp, location = '/' }) => {
  const { lang } = useContext(LangContext);
  const isLanding = location === '/' || location === '/en/';

  const description = descriptionProp || (lang === 'is' ? defaultDescription : defaultDescriptionEn);

  return (
    <Helmet>
      <html lang={lang} dir="ltr" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {isLanding && <link rel="alternate" hrefLang="is" href={`${url}/`} />}
      {isLanding && <link rel="alternate" hrefLang="en" href={`${url}/en/`} />}
      {isLanding && <link rel="alternate" hrefLang="x-default" href={`${url}/`} />}
      <meta name="image" content={image} />

      <meta property="og:url" content={`${url}${location}`} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === 'is' ? 'is_IS' : 'en_GB'} />
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1632" />
      <meta property="og:image:height" content="918" />
      <meta
        property="og:image:alt"
        content={lang === 'is' ? 'Víkin og klettarnir við Hindisvík' : 'The bay and cliffs at Hindisvík'}
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{structuredData(lang, description)}</script>

      <meta name="google-site-verification" content="ZKJbLRpScp5A9umqwvopbgmF1JXvl85tVJXx7i74XVE" />
      <meta name="google-site-verification" content="DSsQmZmACmMGs2qrS5MBaqcRMxMSe9JoLsrps14p9D0" />
    </Helmet>
  );
};
